import { flexRender, type RowData } from "@tanstack/react-table";
import React, { Fragment } from "react";
import { cn } from "../../utils";
import { useDataTableContext } from "./context";
import { DataTableTd } from "./data-table-td";
import type { Row } from "./types";

/** Props Interface */
interface DataTableRowProps<T extends RowData> {
	row: Row<T>;
}

export function DataTableRow<T extends RowData>({ row }: DataTableRowProps<T>) {
	/** Context */
	const { getRowStyles, renderSubRows } = useDataTableContext();

	/** Functions */
	function handleOnClick(e: React.MouseEvent) {
		if (e.metaKey || e.shiftKey) {
			if (row.getCanSelect()) {
				row.toggleSelected();
			}
		}
	}

	/** Computed */
	const { className: rowClassName, ...rowStyle } = getRowStyles?.(row as Row<any>) ?? {};

	/** Render */
	return (
		<Fragment>
			<div
				className={cn("table-row", rowClassName)}
				style={rowStyle}
				onClick={handleOnClick}
			>
				{row.getVisibleCells().map((cell) => (
					<DataTableTd
						key={cell.id}
						cell={cell}
					>
						<div className="line-clamp-1">
							{flexRender(cell.column.columnDef.cell, cell.getContext())}
						</div>
					</DataTableTd>
				))}
			</div>
			{row.getIsExpanded() && renderSubRows && (
				<div
					className={cn(
						"w-full sticky left-0 border-b-[0.5px] border-color bg-primary-50 dark:bg-primary-950 flex flex-col",
					)}
				>
					{renderSubRows(row as Row<any>)}
				</div>
			)}
		</Fragment>
	);
}
